import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Jobs added by admins and the talent team, featured in the weekly digest
// (jobs service; lms-backend docs/api/jobs-posted.md).
const BASE = '/jobs/posted';
const KEY = 'posted-jobs';
export const PAGE_SIZE = 50;

const options = { refetchOnWindowFocus: false, retry: false };

const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const usePostedJobs = (filters, offset = 0) => {
  const params = clean({
    status: filters.status,
    q: filters.q,
    mine: filters.mine ? '1' : undefined,
    limit: PAGE_SIZE,
    offset,
  });
  return useQuery(
    [KEY, 'list', params],
    async () => (await axiosInstancev2.get(BASE, { params })).data,
    { ...options, keepPreviousData: true }
  );
};

export const usePostedJob = (id) =>
  useQuery(
    [KEY, 'one', id],
    async () => (await axiosInstancev2.get(`${BASE}/${id}`)).data,
    { ...options, enabled: Boolean(id) }
  );

const useJobsMutation = (fn) => {
  const queryClient = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => queryClient.invalidateQueries([KEY]),
  });
};

// Create (no id) or update. `force` adds a job despite similar ones.
export const useSavePostedJob = () =>
  useJobsMutation(async ({ id, job, force }) =>
    id
      ? (await axiosInstancev2.put(`${BASE}/${id}`, job)).data
      : (
          await axiosInstancev2.post(
            BASE,
            force ? { ...job, force: true } : job
          )
        ).data
  );

export const useSetPostedJobOpen = () =>
  useJobsMutation(
    async ({ id, open }) =>
      (await axiosInstancev2.post(`${BASE}/${id}/${open ? 'reopen' : 'close'}`))
        .data
  );

// AI suggestions to prefill the form; nothing is saved.
export const useSuggestTags = () =>
  useMutation(
    async (body) =>
      (await axiosInstancev2.post(`${BASE}/tags`, clean(body))).data
  );

export const jobsUnavailable = (error) => {
  const data = error?.response?.data;
  return error?.response?.status === 404 && !(data?.error || data?.message);
};

export const jobsError = (error, fallback) => {
  if (jobsUnavailable(error)) {
    return "Adding jobs isn't available on the server yet.";
  }
  const data = error?.response?.data;
  return data?.error || data?.message || fallback;
};

// 400 problems → { field: message }.
export const jobProblems = (error) =>
  Object.fromEntries(
    (error?.response?.data?.problems ?? []).map((p) => [p.field, p.message])
  );
